import { routes } from '~/constants';
import { relationshipModel, serviceModel, UserRole } from '~/models';
import { RelationshipStatus } from '~/models/relationship';
import { formatDateTimeWithLongMonth } from '~/utils';
import { getTimezoneAbbreviation } from '~/utils/date-timezone.utils';
import { COBStep, customPageStep, customPageStepIndex, getCommonReachableSteps, getStepName, isCustomPageStep, PreviewState, stepsTextsPath, termsOfServiceStepIndex } from './ClientOnboarding.common.utils';
import { hasAtLeastOneServiceCommitted } from './ClientOnboardingInner/ClientOnboardingInner.utils';
export { COBStep, customPageStep, customPageStepIndex, getStepName, isCustomPageStep, PreviewState, stepsTextsPath, termsOfServiceStepIndex };
export function getReachableSteps(relationship) {
    return [
        COBStep.welcome,
        ...getCommonReachableSteps(relationship),
        COBStep.allSet
    ];
}
export function getClientOnboardingEntryRedirect(param) {
    let { relationship, userId, role, isVendor } = param;
    const relationshipId = relationship.id;
    switch(relationship.status){
        case RelationshipStatus.Approved:
            if (relationshipModel.isBuyer(relationship, userId) || relationshipModel.hasClientViewPermission(role)) {
                return {
                    url: routes.agreementMinisiteRoutes(relationshipId).agreement
                };
            }
            return {
                url: routes.noPermissionsRelationshipAlreadyApproved
            };
        case RelationshipStatus.Terminated:
            return relationshipModel.hasClientViewPermission(role) ? {
                url: routes.agreementMinisiteRoutes(relationshipId).agreement
            } : null;
        case RelationshipStatus.Canceled:
            return {
                url: routes.canceledProposalPage
            };
        case RelationshipStatus.Expired:
            return isVendor ? null : {
                url: routes.expiredProposalPage,
                state: {
                    vendorName: relationshipModel.vendorName(relationship)
                }
            };
        default:
            return null;
    }
}
export function isAllowedToGoBack(url) {
    return url === routes.canceledProposalPage;
}
export var Reason = function(Reason) {
    Reason[Reason["PaymentMethod"] = COBStep.paymentMethod] = "PaymentMethod";
    Reason[Reason["ReviewServices"] = COBStep.reviewServices] = "ReviewServices";
    Reason[Reason["SelectAPackage"] = COBStep.selectAPackage] = "SelectAPackage";
    Reason["ApproveTermsOfServices"] = "approveTermsOfServices";
    Reason["ReviewerApprove"] = "reviewerApprove";
    Reason["NotAllowedToSign"] = "notAllowedToSign";
    return Reason;
}({});
export const approveDisabledReasonsTextsPath = 'clientFlow.approveDisabledReasons';
export function reasonToText(reason, texts) {
    const map = {
        [Reason.PaymentMethod]: texts.paymentMethod(),
        [Reason.ReviewServices]: texts.reviewServices(),
        [Reason.SelectAPackage]: texts.selectAPackage(),
        ["reviewerApprove"]: texts.reviewerApprove(),
        ["approveTermsOfServices"]: texts.approveTermsOfServices(),
        ["notAllowedToSign"]: texts.notAllowedToSign()
    };
    return map[reason];
}
export const getNextStepButtonText = (step, relationship, texts)=>{
    const reachableSteps = getReachableSteps(relationship);
    const currentStepIndex = reachableSteps.indexOf(step);
    if (currentStepIndex === -1) {
        throw new Error('Unreachable step');
    }
    const nextStep = reachableSteps[currentStepIndex + 1];
    if (!nextStep) {
        throw new Error('should have next step');
    }
    if (nextStep === COBStep.selectAPackage) {
        return texts.steps.selectAPackage();
    }
    if (nextStep === COBStep.paymentMethod) {
        return texts.steps.paymentMethod();
    }
    switch(step){
        case COBStep.welcome:
            return relationship.isSample ? texts.welcomeStep.reviewTestAgreement() : texts.welcomeStep.reviewTheAgreement();
        default:
            return texts.footer.next();
    }
};
export const getInitialSelectedBundleId = (getFromStorage, relationship, userId)=>{
    const fromStorage = getFromStorage('selected_bundle_id');
    if (fromStorage && !relationshipModel.isSignatory(relationship, userId) && !relationshipModel.isReviewer(relationship, userId)) {
        return fromStorage;
    }
    return relationshipModel.selectedBundleId(relationship) || undefined;
};
export const getInitialSelectedOptionalServices = (param)=>{
    let { setToStorage, getFromStorage, isPreview, relationship, selectedBundleID, userId } = param;
    const getInitialSelectedOptionalServices = ()=>{
        const initialSelectedOptionalServices = relationshipModel.includedByDefaultOptionalServices(relationship, selectedBundleID);
        setToStorage('selected_optional_services', initialSelectedOptionalServices);
        return initialSelectedOptionalServices;
    };
    const fromStorage = getFromStorage('selected_optional_services');
    const fromRelationship = relationshipModel.selectedOptionalServices(relationship);
    const hasSelectedOptionalServices = fromRelationship.length > 0;
    if (isPreview) {
        return getInitialSelectedOptionalServices();
    }
    if (fromStorage && !relationshipModel.isSignatory(relationship, userId) && !relationshipModel.isReviewer(relationship, userId)) {
        return fromStorage;
    }
    if (hasSelectedOptionalServices) {
        setToStorage('selected_optional_services', fromRelationship);
        return fromRelationship;
    }
    return getInitialSelectedOptionalServices();
};
export const getInitialSelectedPaymentMethod = (getFromStorage, relationship, userId)=>{
    const selectedPaymentFromStorage = getFromStorage('selected_payment_method');
    if (selectedPaymentFromStorage && !relationshipModel.isSignatory(relationship, userId) && !relationshipModel.isReviewer(relationship, userId)) {
        return selectedPaymentFromStorage;
    } else {
        return {
            fundingSourceId: relationshipModel.selectedFundingSourceId(relationship) || undefined
        };
    }
};
export function isApproveProposalDisabled(disabledReason, isPreview, relationship, userId, role) {
    if (disabledReason) {
        return true;
    }
    return relationship.isSample || isPreview ? isPreview : !getIsAllowedToSIgn({
        relationship,
        userId,
        role
    });
}
export function getIsAllowedToSIgn(param) {
    let { relationship, userId, role } = param;
    const isBuyerAdmin = relationshipModel.isBuyer(relationship, userId) && isAdminOrOwner(role);
    if (relationshipModel.hasSignatories(relationship)) {
        return relationshipModel.isSignatory(relationship, userId) || isBuyerAdmin;
    }
    return isBuyerAdmin;
}
function isAdminOrOwner(role) {
    return role === UserRole.Admin || role === UserRole.Owner;
}
export const limitedTimeDiscountTextsPath = 'clientFlow.limitedTimeDiscount';
export function getExpirationDateMessage(expirationDate, texts) {
    if (!expirationDate) return null;
    const utcMidnight = new Date(Date.UTC(expirationDate.year, expirationDate.month - 1, expirationDate.day + 1, 0, 0, 0, 0));
    const timezoneAbbreviation = getTimezoneAbbreviation(utcMidnight);
    return texts.withDateAndTimezone({
        date: formatDateTimeWithLongMonth(utcMidnight),
        timezone: timezoneAbbreviation || texts.yourTimezone()
    });
}
export function getLimitedTimeDiscountOfferCalloutTextForService(service, texts) {
    const offer = serviceModel.limitedTimeDiscountOffer(service);
    if (!offer) return null;
    const expirationDate = offer.ExpirationDate();
    if (expirationDate) {
        return getExpirationDateMessage(expirationDate, texts);
    }
    const days = offer.ExpirationDays();
    return days ? texts.withDays({
        days
    }) : null;
}
export function computeApproveDisabledReason(relationship, reachableSteps, selectedPayment, selectedBundleID, selectedOptionalServices, termsOfServiceStatements) {
    if (!relationship.isSample && reachableSteps.includes(COBStep.paymentMethod) && !(selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId)) {
        return Reason.PaymentMethod;
    }
    if (reachableSteps.includes(COBStep.selectAPackage) && !selectedBundleID) {
        return Reason.SelectAPackage;
    }
    if (termsOfServiceStatements.includes(false)) {
        return "approveTermsOfServices";
    }
    if (!hasAtLeastOneServiceCommitted(relationship, selectedBundleID, selectedOptionalServices)) {
        return Reason.ReviewServices;
    }
    return undefined;
}
